import { resolveShowId } from "./showFields";
import { formatReleaseDate } from "./formatReleaseDate";

export const WATCHLIST_STORAGE_VERSION = 2;

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const normalizeWatchlistId = (showOrId) => {
  const id = typeof showOrId === "object" && showOrId !== null
    ? resolveShowId(showOrId) || showOrId.id
    : showOrId;

  if (id === null || id === undefined || id === "") {
    return null;
  }

  return String(id);
};

const compactArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(Boolean);
};

export const createWatchlistEntry = (showOrId, existing = null) => {
  const id = normalizeWatchlistId(showOrId);
  if (!id) {
    return null;
  }

  const show = typeof showOrId === "object" && showOrId !== null ? showOrId : {};
  return {
    id,
    title: show.title || existing?.title || null,
    slug: show.slug || existing?.slug || null,
    poster: show.poster || existing?.poster || null,
    releaseDate: show.releaseDate || existing?.releaseDate || null,
    networks: compactArray(show.networks).length ? compactArray(show.networks) : compactArray(existing?.networks),
    sourceName: show.sourceName || existing?.sourceName || null,
    year: show.year || existing?.year || null,
    seasonNumber: show.seasonNumber ?? existing?.seasonNumber ?? null,
    ...(show.seasonPart ? { seasonPart: show.seasonPart } : {}),
    genres: compactArray(show.genres).length ? compactArray(show.genres) : compactArray(existing?.genres),
    detailsUrl: show.detailsUrl || existing?.detailsUrl || null,
    imdbId: show.imdbId || show.imdb_id || show.omdbData?.imdbId || show.omdbData?.imdbID || existing?.imdbId || null,
    tvdbId: show.tvdbId || show.tvdb_id || existing?.tvdbId || null,
    nextWatch: show.nextWatch || existing?.nextWatch || null,
    addedAt: existing?.addedAt || show.addedAt || new Date().toISOString(),
  };
};

export const normalizeWatchlistEntry = (entry) => {
  if (entry === null || entry === undefined) {
    return null;
  }

  if (typeof entry !== "object") {
    return createWatchlistEntry(entry);
  }

  return createWatchlistEntry(entry, entry);
};

export const normalizeWatchlistEntries = (entries = []) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  const seen = new Set();
  return entries
    .map(normalizeWatchlistEntry)
    .filter(Boolean)
    .filter((entry) => {
      if (seen.has(entry.id)) {
        return false;
      }
      seen.add(entry.id);
      return true;
    });
};

const parseReleaseDate = (dateString) => {
  if (typeof dateString !== "string" || !DATE_PATTERN.test(dateString)) {
    return null;
  }

  const [year, month, day] = dateString.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

export const getTodayDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const getReleaseRadarLabel = (releaseDate, today = getTodayDate()) => {
  const date = parseReleaseDate(releaseDate);
  if (!date) {
    return "Date TBA";
  }

  const diffDays = Math.round((date.getTime() - today.getTime()) / 86400000);
  if (diffDays === 0) {
    return "Premieres today";
  }
  if (diffDays === 1) {
    return "Premieres tomorrow";
  }
  if (diffDays > 1) {
    return `Premieres in ${diffDays} days`;
  }
  if (diffDays === -1) {
    return "Premiered yesterday";
  }
  return `Premiered ${Math.abs(diffDays)} days ago`;
};

export const splitReleaseRadarEntries = (entries = [], today = getTodayDate()) => {
  const normalized = normalizeWatchlistEntries(entries);
  const withDates = normalized.map((entry) => ({
    ...entry,
    releaseDateObject: parseReleaseDate(entry.releaseDate),
  }));

  const awaiting = withDates
    .filter((entry) => entry.releaseDateObject && entry.releaseDateObject >= today)
    .sort((a, b) => a.releaseDateObject - b.releaseDateObject);

  const ready = withDates
    .filter((entry) => !entry.releaseDateObject || entry.releaseDateObject < today)
    .sort((a, b) => {
      if (!a.releaseDateObject && !b.releaseDateObject) {
        return (b.addedAt || "").localeCompare(a.addedAt || "");
      }
      if (!a.releaseDateObject) {
        return 1;
      }
      if (!b.releaseDateObject) {
        return -1;
      }
      return b.releaseDateObject - a.releaseDateObject;
    });

  return {
    awaiting: awaiting.map((entry) => {
      const nextEntry = { ...entry };
      delete nextEntry.releaseDateObject;
      return nextEntry;
    }),
    ready: ready.map((entry) => {
      const nextEntry = { ...entry };
      delete nextEntry.releaseDateObject;
      return nextEntry;
    }),
  };
};

// Accessible name for a save toggle. It stays the same in both states; aria-pressed carries the state.
export const getWatchlistToggleLabel = (title) => {
  const name = typeof title === "string" && title.trim() ? title.trim() : "show";
  return `Save ${name} to My List`;
};

export const getWatchlistAnnouncement = (showOrId, saved) => {
  const title = typeof showOrId?.title === "string" ? showOrId.title.trim() : "";
  const name = title || "show";
  return saved ? `Saved ${name} to My List` : `Removed ${name} from My List`;
};

const showName = (show) => {
  const title = typeof show?.title === "string" ? show.title.trim() : "";
  return title || "show";
};

export const getRemovalAnnouncement = (show) => `Removed ${showName(show)}. Undo is available.`;

export const getRestoreAnnouncement = (show) => `Restored ${showName(show)}.`;

// Where a removal leaves the page: the section the row sat in, the row that followed it there
// (where the Undo notice goes), and the row that should receive focus - the next one, else the previous.
export const planRemoval = ({ awaiting = [], ready = [] }, id) => {
  const order = [...awaiting, ...ready];
  const index = order.findIndex((entry) => entry.id === id);
  if (index === -1) {
    return null;
  }
  const section = awaiting.some((entry) => entry.id === id) ? "awaiting" : "ready";
  const rows = section === "awaiting" ? awaiting : ready;
  const beforeId = rows[rows.findIndex((entry) => entry.id === id) + 1]?.id ?? null;
  const focusId = (order[index + 1] ?? order[index - 1])?.id ?? null;
  return { section, beforeId, focusId };
};

const OLD_RELEASE_DAYS = 60;

// "4 Mar" while a date is recent or ahead; "4 Mar 2025" once it is more than about 60 days old.
export const formatSavedReleaseDate = (releaseDate, today = getTodayDate()) => {
  const date = parseReleaseDate(releaseDate);
  if (!date) {
    return "";
  }
  const label = formatReleaseDate(releaseDate);
  const ageInDays = Math.round((today.getTime() - date.getTime()) / 86400000);
  return ageInDays > OLD_RELEASE_DAYS ? `${label} ${date.getFullYear()}` : label;
};
